/** The DOM side of cents-first entry (cents.ts): document listeners and setCents(). main.ts only. */
import { money, parseCents } from './format';
import { CENTS_MAX_DIGITS, centsStart, digitsOf, editCents, formatDigits, type CentsEdit, type CentsState } from './cents';

const isCents = (t: EventTarget | null): t is HTMLInputElement => t instanceof HTMLInputElement && t.hasAttribute('data-cents');

function stateOf(input: HTMLInputElement): CentsState {
  return { value: input.value, pristine: input.dataset.pristine === '1', decimal: input.dataset.centsMode === 'decimal' };
}

function show(input: HTMLInputElement, s: CentsState) {
  input.value = s.value;
  if (s.pristine) input.dataset.pristine = '1';
  else delete input.dataset.pristine;
  if (s.decimal) input.dataset.centsMode = 'decimal';
  else delete input.dataset.centsMode;
  const end = s.value.length;
  try {
    input.setSelectionRange(end, end);
  } catch {
    /* not focused */
  }
  say(input);
}

/** The live region next to the field says the amount as it now reads ("$10.46"). */
function say(input: HTMLInputElement) {
  const said = document.getElementById(`${input.id}-said`);
  if (!said) return;
  const c = parseCents(input.value);
  said.textContent = money(Number.isFinite(c) && c > 0 ? c : 0, 2);
}

/** Sets an amount from code (a category or item pick): pre-filled again, cents-first again. */
export function setCents(input: HTMLInputElement, value: string) {
  show(input, centsStart(value));
}

/** document `beforeinput`: every keystroke, delete and paste on a cents field goes through editCents(). */
export function onCentsBeforeInput(e: InputEvent) {
  const input = e.target;
  if (!isCents(input)) return;
  const all = input.value !== '' && input.selectionStart === 0 && input.selectionEnd === input.value.length;
  const t = e.inputType;
  let edit: CentsEdit | null = null;
  if (t === 'insertText') edit = { kind: 'insert', text: e.data ?? '', all };
  else if (t.startsWith('delete')) edit = { kind: 'delete', all };
  else if (t === 'insertFromPaste' || t === 'insertFromDrop' || t === 'insertReplacementText')
    edit = { kind: 'paste', text: e.data ?? e.dataTransfer?.getData('text/plain') ?? '' };
  const s = stateOf(input);
  if (!edit) {
    // Anything else (an IME composition) is cleaned up afterwards by onCentsInput().
    if (!s.decimal && e.cancelable && t !== 'insertCompositionText') e.preventDefault();
    return;
  }
  const next = editCents(s, edit);
  if (!next) {
    delete input.dataset.pristine; // decimal mode: the browser types it
    return;
  }
  e.preventDefault();
  if (next === s) return;
  show(input, next);
  // Other listeners (the "typed, so no pick replaces it" rule) still hear an input event.
  input.dispatchEvent(new Event('input', { bubbles: true }));
}

/** document `input`: keeps a cents field formatted whatever got through (autofill, composition). */
export function onCentsInput(e: Event) {
  const input = e.target;
  if (!isCents(input)) return;
  if (input.dataset.centsMode === 'decimal' || /^(\d+\.\d{2})?$/.test(input.value)) return say(input);
  show(input, { value: formatDigits(digitsOf(input.value).slice(0, CENTS_MAX_DIGITS)), pristine: false, decimal: false });
}
